import React, { useState, useCallback, useMemo, useEffect } from "react";
import { getLogsURL } from "../../../const";
import "../common.css";
import "./index.css";

const TODAY = new Date().toISOString().slice(0, 10);
const YESTERDAY = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const LOG_TYPE_META = {
  s: { label: "Success", className: "log-badge success" },
  e: { label: "Error", className: "log-badge error" },
  i: { label: "Info", className: "log-badge info" },
  "page opened": { label: "Page Opened", className: "log-badge page" },
  w: { label: "Warning", className: "log-badge warning" },
};

function getBadgeMeta(rawType) {
  const key = (rawType || "").toLowerCase().trim();
  return LOG_TYPE_META[key] || { label: rawType || "—", className: "log-badge default" };
}

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "medium",
    });
  } catch {
    return iso;
  }
}

function exportCSV(rows) {
  const headers = ["#", "Timestamp", "Module", "Program", "User", "Type", "Content"];
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [
    headers.join(","),
    ...rows.map((r, i) =>
      [
        i + 1,
        escape(r.timestamp),
        escape(r.module_name),
        escape(r.program_name),
        escape(r.user),
        escape(r.log_type),
        escape(r.content),
      ].join(",")
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `logs-${TODAY}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const AdminLogs = () => {
  const [fromDate, setFromDate] = useState(YESTERDAY);
  const [toDate, setToDate] = useState(TODAY);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fetched, setFetched] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const fetchLogs = useCallback(async () => {
    if (!fromDate || !toDate) {
      setError("Please select both From date and To date.");
      return;
    }
    setLoading(true);
    setError("");
    setLogs([]);
    setFetched(false);
    setPage(1);
    try {
      const res = await fetch(getLogsURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
        },
        body: JSON.stringify({ from_date: fromDate, to_date: toDate }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      const entries = Array.isArray(data) ? data : data.logs ?? data.data ?? [];
      setLogs(entries);
      setFetched(true);
    } catch (err) {
      setError(err.message || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unique values for dropdowns
  const allTypes = useMemo(() => {
    const s = new Set(logs.map((r) => (r.log_type || "").toLowerCase().trim()).filter(Boolean));
    return Array.from(s).sort();
  }, [logs]);

  const allModules = useMemo(() => {
    const s = new Set(logs.map((r) => r.module_name || "").filter(Boolean));
    return Array.from(s).sort();
  }, [logs]);

  const allUsers = useMemo(() => {
    const s = new Set(logs.map((r) => r.user || "").filter(Boolean));
    return Array.from(s).sort();
  }, [logs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return logs.filter((row) => {
      if (typeFilter !== "all" && (row.log_type || "").toLowerCase().trim() !== typeFilter) return false;
      if (moduleFilter !== "all" && row.module_name !== moduleFilter) return false;
      if (userFilter !== "all" && row.user !== userFilter) return false;
      if (q) {
        const haystack = [row.module_name, row.program_name, row.user, row.content, row.timestamp, row.log_type]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [logs, search, typeFilter, moduleFilter, userFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const resetFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setModuleFilter("all");
    setUserFilter("all");
    setPage(1);
  };

  const hasActiveFilters = search || typeFilter !== "all" || moduleFilter !== "all" || userFilter !== "all";

  return (
    <div className="admin-page-container">
      <div className="admin-page-content logs-content">
        <div className="header-section">
          <h1 className="page-title">Activity Logs</h1>
          <p className="page-subtitle">View user activity across all modules with date-range and column filtering</p>
        </div>

        {/* Date range fetch bar */}
        <div className="logs-filter-bar">
          <div className="logs-date-group">
            <label className="logs-label">From date</label>
            <input
              type="date"
              className="admin-input logs-date-input"
              value={fromDate}
              max={toDate || TODAY}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="logs-date-group">
            <label className="logs-label">To date</label>
            <input
              type="date"
              className="admin-input logs-date-input"
              value={toDate}
              min={fromDate || undefined}
              max={TODAY}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="admin-btn primary logs-fetch-btn"
            onClick={fetchLogs}
            disabled={loading}
          >
            {loading ? "Loading…" : "Load Logs"}
          </button>
          {fetched && filtered.length > 0 && (
            <button
              type="button"
              className="admin-btn secondary logs-export-btn"
              onClick={() => exportCSV(filtered)}
            >
              Export CSV
            </button>
          )}
        </div>

        {error && <div className="logs-error">{error}</div>}

        {/* Column filters */}
        {fetched && (
          <div className="logs-column-filters">
            <div className="logs-filter-group">
              <label className="logs-label">Search</label>
              <input
                type="text"
                className="admin-input logs-search-input"
                placeholder="Module, user, content…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <div className="logs-filter-group">
              <label className="logs-label">Log type</label>
              <select
                className="admin-input logs-filter-select"
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All types</option>
                {allTypes.map((t) => (
                  <option key={t} value={t}>{getBadgeMeta(t).label}</option>
                ))}
              </select>
            </div>
            <div className="logs-filter-group">
              <label className="logs-label">Module</label>
              <select
                className="admin-input logs-filter-select logs-filter-select--wide"
                value={moduleFilter}
                onChange={(e) => { setModuleFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All modules</option>
                {allModules.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="logs-filter-group">
              <label className="logs-label">User</label>
              <select
                className="admin-input logs-filter-select logs-filter-select--wide"
                value={userFilter}
                onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All users</option>
                {allUsers.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
            <div className="logs-filter-group logs-filter-group--actions">
              {hasActiveFilters && (
                <button type="button" className="admin-btn secondary logs-reset-btn" onClick={resetFilters}>
                  Clear filters
                </button>
              )}
              <span className="logs-count">
                <strong>{filtered.length}</strong> of <strong>{logs.length}</strong> entries
              </span>
            </div>
          </div>
        )}

        {loading && (
          <div className="logs-state-msg">
            <div className="logs-spinner" />
            <span>Fetching logs…</span>
          </div>
        )}

        {fetched && !loading && filtered.length === 0 && (
          <p className="admin-empty">No log entries match the current filters.</p>
        )}

        {fetched && !loading && filtered.length > 0 && (
          <>
            <div className="admin-table-wrap logs-table-wrap">
              <table className="admin-table logs-table">
                <thead>
                  <tr>
                    <th className="logs-th-num">#</th>
                    <th>Timestamp</th>
                    <th>Module</th>
                    <th>Program</th>
                    <th>User</th>
                    <th>Type</th>
                    <th>Content</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((row, i) => {
                    const badge = getBadgeMeta(row.log_type);
                    const isError = (row.log_type || "").toLowerCase() === "e";
                    const globalIdx = (page - 1) * pageSize + i + 1;
                    return (
                      <tr key={`${row.timestamp}-${i}`} className={isError ? "log-row-error" : ""}>
                        <td className="logs-td-num">{globalIdx}</td>
                        <td className="logs-td-ts">{formatTimestamp(row.timestamp)}</td>
                        <td className="logs-td-module">{row.module_name || "—"}</td>
                        <td className="logs-td-program" title={row.program_name}>{row.program_name || "—"}</td>
                        <td className="logs-td-user">{row.user || "—"}</td>
                        <td className="logs-td-type">
                          <span className={badge.className}>{badge.label}</span>
                        </td>
                        <td className="logs-td-content">{row.content || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination bar */}
            <div className="logs-pagination">
              <div className="logs-page-size">
                <label className="logs-label">Rows per page</label>
                <select
                  className="admin-input logs-page-size-select"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                >
                  {PAGE_SIZE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="logs-page-nav">
                <button
                  className="logs-page-btn"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  title="First page"
                >
                  «
                </button>
                <button
                  className="logs-page-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  ‹
                </button>
                <span className="logs-page-info">
                  Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                </span>
                <button
                  className="logs-page-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  ›
                </button>
                <button
                  className="logs-page-btn"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  title="Last page"
                >
                  »
                </button>
              </div>
            </div>
          </>
        )}

        {!fetched && !loading && (
          <div className="logs-empty-state">
            <div className="logs-empty-icon">📋</div>
            <p>Select a date range and click <strong>Load Logs</strong> to view activity.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogs;
